/**
 * 地理坐标系统管理器
 * 负责处理WGS84经纬度坐标与Three.js 3D世界坐标的转换
 * 支持UTM投影和本地坐标系转换，精度<1米
 */

import * as THREE from 'three';
import proj4 from 'proj4';

// 定义坐标接口
export interface GeographicCoordinate {
  longitude: number; // 经度 (WGS84)
  latitude: number; // 纬度 (WGS84)
  altitude?: number; // 海拔高度 (米)
}

export interface LocalCoordinate {
  x: number; // 本地X坐标 (米)
  y: number; // 本地Y坐标 (米)
  z: number; // 本地Z坐标 (米)
}

export interface World3DCoordinate {
  x: number; // Three.js世界X坐标
  y: number; // Three.js世界Y坐标
  z: number; // Three.js世界Z坐标
}

// UTM投影配置
export interface UTMConfig {
  zone: number; // UTM区域号 (1-60)
  hemisphere: 'north' | 'south'; // 半球
}

export class CoordinateSystem {
  private originLat: number = 0;
  private originLng: number = 0;
  private originAlt: number = 0;
  private scale: number = 1;
  private utmConfig: UTMConfig;
  private utmProjection: string;
  private wgs84Projection: string = 'EPSG:4326'; // WGS84

  constructor(
    originLat: number,
    originLng: number,
    originAlt: number = 0,
    scale: number = 1
  ) {
    this.originLat = originLat;
    this.originLng = originLng;
    this.originAlt = originAlt;
    this.scale = scale;

    // 自动计算UTM区域
    this.utmConfig = this.calculateUTMZone(originLat, originLng);
    this.utmProjection = this.getUTMProjectionString(this.utmConfig);

    console.log(`[CoordinateSystem] 初始化完成:`, {
      origin: { lat: originLat, lng: originLng, alt: originAlt },
      scale,
      utm: this.utmConfig,
      projection: this.utmProjection,
    });
  }

  /**
   * 自动计算UTM区域
   */
  private calculateUTMZone(lat: number, lng: number): UTMConfig {
    const zone = Math.floor((lng + 180) / 6) + 1;
    const hemisphere = lat >= 0 ? 'north' : 'south';
    return { zone, hemisphere };
  }

  /**
   * 获取UTM投影字符串
   */
  private getUTMProjectionString(config: UTMConfig): string {
    const epsgCode =
      config.hemisphere === 'north'
        ? 32600 + config.zone // UTM北半球
        : 32700 + config.zone; // UTM南半球
    return `EPSG:${epsgCode}`;
  }

  /**
   * WGS84经纬度转换为UTM坐标
   */
  private geographicToUTM(coord: GeographicCoordinate): {
    x: number;
    y: number;
  } {
    try {
      const result = proj4(this.wgs84Projection, this.utmProjection, [
        coord.longitude,
        coord.latitude,
      ]);
      return { x: result[0], y: result[1] };
    } catch (error) {
      console.error('[CoordinateSystem] UTM转换失败:', error);
      throw new Error(`坐标转换失败: ${error}`);
    }
  }

  /**
   * UTM坐标转换为WGS84经纬度
   */
  private utmToGeographic(x: number, y: number): GeographicCoordinate {
    try {
      const result = proj4(this.utmProjection, this.wgs84Projection, [x, y]);
      return { longitude: result[0], latitude: result[1] };
    } catch (error) {
      console.error('[CoordinateSystem] 地理坐标转换失败:', error);
      throw new Error(`坐标转换失败: ${error}`);
    }
  }

  /**
   * 地理坐标转换为本地坐标
   */
  public geographicToLocal(coord: GeographicCoordinate): LocalCoordinate {
    // 1. 转换为UTM坐标
    const utmCoord = this.geographicToUTM(coord);
    const originUTM = this.geographicToUTM({
      longitude: this.originLng,
      latitude: this.originLat,
    });

    // 2. 计算相对偏移
    const localX = (utmCoord.x - originUTM.x) * this.scale;
    const localY = (utmCoord.y - originUTM.y) * this.scale;
    const localZ = ((coord.altitude || 0) - this.originAlt) * this.scale;

    return { x: localX, y: localY, z: localZ };
  }

  /**
   * 本地坐标转换为地理坐标
   */
  public localToGeographic(coord: LocalCoordinate): GeographicCoordinate {
    // 1. 获取原点的UTM坐标
    const originUTM = this.geographicToUTM({
      longitude: this.originLng,
      latitude: this.originLat,
    });

    // 2. 计算目标UTM坐标
    const targetUTMX = originUTM.x + coord.x / this.scale;
    const targetUTMY = originUTM.y + coord.y / this.scale;

    // 3. 转换为地理坐标
    const geoCoord = this.utmToGeographic(targetUTMX, targetUTMY);

    return {
      longitude: geoCoord.longitude,
      latitude: geoCoord.latitude,
      altitude: this.originAlt + coord.z / this.scale,
    };
  }

  /**
   * 本地坐标转换为Three.js世界坐标
   * 注意：Three.js使用右手坐标系，Y轴向上
   */
  public localToWorld3D(coord: LocalCoordinate): World3DCoordinate {
    return {
      x: coord.x,
      y: coord.z, // 本地Z轴映射到世界Y轴（高度）
      z: -coord.y, // 本地Y轴映射到世界-Z轴（北方向为负Z）
    };
  }

  /**
   * Three.js世界坐标转换为本地坐标
   */
  public world3DToLocal(coord: World3DCoordinate): LocalCoordinate {
    return {
      x: coord.x,
      y: -coord.z, // 世界-Z轴映射到本地Y轴
      z: coord.y, // 世界Y轴映射到本地Z轴（高度）
    };
  }

  /**
   * 地理坐标直接转换为Three.js世界坐标（组合转换）
   */
  public geographicToWorld3D(coord: GeographicCoordinate): World3DCoordinate {
    const localCoord = this.geographicToLocal(coord);
    return this.localToWorld3D(localCoord);
  }

  /**
   * Three.js世界坐标直接转换为地理坐标（组合转换）
   */
  public world3DToGeographic(coord: World3DCoordinate): GeographicCoordinate {
    const localCoord = this.world3DToLocal(coord);
    return this.localToGeographic(localCoord);
  }

  /**
   * 创建Three.js Vector3对象
   */
  public createVector3FromGeographic(
    coord: GeographicCoordinate
  ): THREE.Vector3 {
    const world3D = this.geographicToWorld3D(coord);
    return new THREE.Vector3(world3D.x, world3D.y, world3D.z);
  }

  /**
   * 从Three.js Vector3获取地理坐标
   */
  public getGeographicFromVector3(vector: THREE.Vector3): GeographicCoordinate {
    return this.world3DToGeographic({ x: vector.x, y: vector.y, z: vector.z });
  }

  /**
   * 计算两个地理坐标之间的距离（米）
   */
  public calculateDistance(
    coord1: GeographicCoordinate,
    coord2: GeographicCoordinate
  ): number {
    const local1 = this.geographicToLocal(coord1);
    const local2 = this.geographicToLocal(coord2);

    const dx = local2.x - local1.x;
    const dy = local2.y - local1.y;
    const dz = local2.z - local1.z;

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /**
   * 获取坐标系统信息
   */
  public getInfo() {
    return {
      origin: {
        latitude: this.originLat,
        longitude: this.originLng,
        altitude: this.originAlt,
      },
      scale: this.scale,
      utmZone: this.utmConfig,
      projection: this.utmProjection,
    };
  }

  /**
   * 更新坐标系原点
   */
  public updateOrigin(lat: number, lng: number, alt: number = 0) {
    this.originLat = lat;
    this.originLng = lng;
    this.originAlt = alt;

    // 重新计算UTM区域
    this.utmConfig = this.calculateUTMZone(lat, lng);
    this.utmProjection = this.getUTMProjectionString(this.utmConfig);

    console.log(`[CoordinateSystem] 原点已更新:`, this.getInfo());
  }

  /**
   * 设置缩放比例
   */
  public setScale(scale: number) {
    this.scale = scale;
    console.log(`[CoordinateSystem] 缩放比例已更新: ${scale}`);
  }
}

// 导出默认实例创建函数
export function createCoordinateSystem(
  originLat: number,
  originLng: number,
  originAlt: number = 0,
  scale: number = 1
): CoordinateSystem {
  return new CoordinateSystem(originLat, originLng, originAlt, scale);
}
